import { useEffect, useState, useMemo, useCallback } from "react";
import "@/App.css";
import axios from "axios";
import { useDropzone } from "react-dropzone";
import {
  Database, FolderPlus, Folder, Upload, FileText, FileJson, FileAudio, FileVideo,
  Sparkles, Table as TableIcon, Server, Download, Search, Trash2, RefreshCw,
  Plus, Send, Check, X, Loader2, Cpu, Wand2, Settings2, ChevronRight,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---------- tiny toast ----------
function useToast() {
  const [msg, setMsg] = useState(null);
  const show = (m, kind = "ok") => { setMsg({ m, kind }); setTimeout(() => setMsg(null), 3500); };
  const node = msg ? (
    <div data-testid="toast" className={`fixed bottom-6 right-6 z-50 px-4 py-2.5 rounded-md shadow-lg text-sm border ${msg.kind === "err" ? "bg-red-50 text-red-700 border-red-200" : "bg-zinc-900 text-white border-zinc-800"}`}>
      {msg.m}
    </div>
  ) : null;
  return { show, node };
}

// ---------- Sidebar ----------
const NAV = [
  { key: "projects", label: "Projects", icon: Folder },
  { key: "raw", label: "Raw Data", icon: Database },
  { key: "architect", label: "Dataset Architect", icon: Wand2 },
  { key: "editor", label: "Dataset Editor", icon: TableIcon },
  { key: "search", label: "DeepLake Search", icon: Search },
  { key: "providers", label: "AI Providers", icon: Server },
  { key: "export", label: "Export", icon: Download },
];

function Sidebar({ view, setView, project }) {
  return (
    <aside className="w-64 flex-shrink-0 border-r border-zinc-200 bg-zinc-50 h-screen sticky top-0 flex flex-col" data-testid="sidebar">
      <div className="px-5 pt-6 pb-5 border-b border-zinc-200">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded bg-black text-white flex items-center justify-center">
            <Cpu size={16} />
          </div>
          <div>
            <div className="font-display font-bold text-[15px] text-zinc-950 leading-tight">AI Training</div>
            <div className="font-display font-bold text-[15px] text-zinc-950 leading-tight -mt-0.5">Studio</div>
          </div>
        </div>
      </div>
      <div className="px-3 pt-4 pb-2">
        <div className="overline px-2 mb-2">Active Project</div>
        <div className="px-2.5 py-2 bg-white border border-zinc-200 rounded-md text-sm text-zinc-900 truncate" data-testid="active-project-name">
          {project ? project.name : <span className="text-zinc-400">— none —</span>}
        </div>
      </div>
      <nav className="flex-1 px-2 py-3 overflow-y-auto scrollbar-thin">
        <div className="overline px-3 mb-2">Workflow</div>
        {NAV.map(({ key, label, icon: Icon }) => {
          const active = view === key;
          const disabled = !project && key !== "projects" && key !== "providers";
          return (
            <button
              key={key}
              data-testid={`nav-${key}`}
              disabled={disabled}
              onClick={() => setView(key)}
              className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm mb-0.5 transition-colors
                ${active ? "bg-zinc-900 text-white" : "text-zinc-700 hover:bg-zinc-100"}
                ${disabled ? "opacity-40 cursor-not-allowed hover:bg-transparent" : ""}`}
            >
              <Icon size={15} />
              <span>{label}</span>
              {active && <ChevronRight size={14} className="ml-auto" />}
            </button>
          );
        })}
      </nav>
      <div className="px-4 py-4 border-t border-zinc-200 text-[11px] text-zinc-500 font-mono">
        DeepLake · Whisper · Multi-provider
      </div>
    </aside>
  );
}

// ---------- Projects ----------
function ProjectsView({ projects, reload, onOpen, toast }) {
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState(false);

  const create = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/projects`, { name, description: desc });
      setName(""); setDesc("");
      await reload();
      onOpen(r.data);
      toast.show("Project created");
    } catch (e) { toast.show("Failed to create", "err"); }
    setBusy(false);
  };
  const del = async (id) => {
    if (!window.confirm("Delete project and all its data?")) return;
    await axios.delete(`${API}/projects/${id}`);
    await reload();
    toast.show("Deleted");
  };

  return (
    <div className="p-8 max-w-[1200px]">
      <Header title="Projects" subtitle="A project holds raw files, DeepLake vectors, and dataset rows. Create one per training target." />
      <div className="card p-5 mb-8">
        <div className="overline mb-3">New Project</div>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr_auto] gap-3 items-end">
          <div>
            <label className="label">Name</label>
            <input data-testid="new-project-name" className="input" placeholder="Sarcastic-Assistant-v1" value={name} onChange={e => setName(e.target.value)} />
          </div>
          <div>
            <label className="label">Description</label>
            <input data-testid="new-project-desc" className="input" placeholder="What is this dataset for?" value={desc} onChange={e => setDesc(e.target.value)} />
          </div>
          <button data-testid="create-project-btn" className="btn-primary" onClick={create} disabled={busy || !name.trim()}>
            <FolderPlus size={15} /> Create
          </button>
        </div>
      </div>
      <div className="overline mb-3">All Projects ({projects.length})</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {projects.map(p => (
          <div key={p.id} className="card p-4 hover:shadow-sm transition-shadow" data-testid={`project-card-${p.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-display font-semibold text-zinc-950 truncate">{p.name}</div>
                <div className="text-xs text-zinc-500 mt-0.5 line-clamp-2 min-h-[2em]">{p.description || "No description"}</div>
              </div>
              <button className="btn-ghost !p-1.5" onClick={() => del(p.id)} data-testid={`delete-project-${p.id}`}>
                <Trash2 size={14} className="text-zinc-400 hover:text-red-600" />
              </button>
            </div>
            <div className="mt-3 text-[11px] font-mono text-zinc-500">{p.id.slice(0, 8)}</div>
            <button className="btn-outline w-full mt-4 justify-center" onClick={() => onOpen(p)} data-testid={`open-project-${p.id}`}>
              Open <ChevronRight size={14} />
            </button>
          </div>
        ))}
        {projects.length === 0 && (
          <div className="col-span-full text-center text-zinc-400 py-12 text-sm">No projects yet. Create one above.</div>
        )}
      </div>
    </div>
  );
}

function Header({ title, subtitle, right }) {
  return (
    <div className="mb-6 flex items-end justify-between gap-4 flex-wrap">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-zinc-950">{title}</h1>
        {subtitle && <p className="text-sm text-zinc-500 mt-1.5 max-w-2xl">{subtitle}</p>}
      </div>
      {right}
    </div>
  );
}

function StatusBadge({ s }) {
  const map = { ready: "badge-green", processing: "badge-amber", pending: "badge-zinc", failed: "badge-red" };
  return <span className={`badge ${map[s] || "badge-zinc"}`}>{s}</span>;
}

function fileIcon(t) {
  if (t === "audio") return <FileAudio size={14} />;
  if (t === "video") return <FileVideo size={14} />;
  if (t === "json") return <FileJson size={14} />;
  return <FileText size={14} />;
}

// ---------- Raw Data ----------
function RawDataView({ project, toast }) {
  const [files, setFiles] = useState([]);
  const [textTitle, setTextTitle] = useState("");
  const [textContent, setTextContent] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const reload = useCallback(async () => {
    const r = await axios.get(`${API}/projects/${project.id}/raw_files`);
    setFiles(r.data);
  }, [project.id]);

  useEffect(() => { reload(); }, [reload]);
  useEffect(() => {
    const has = files.some(f => f.status === "processing" || f.status === "pending");
    if (!has) return;
    const t = setInterval(reload, 2000);
    return () => clearInterval(t);
  }, [files, reload]);

  const onDrop = useCallback(async (accepted) => {
    if (!accepted.length) return;
    setUploading(true);
    for (const file of accepted) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        await axios.post(`${API}/projects/${project.id}/ingest`, fd);
      } catch (e) { toast.show(`Upload failed: ${file.name}`, "err"); }
    }
    setUploading(false);
    await reload();
    toast.show(`${accepted.length} file(s) queued`);
  }, [project.id, reload, toast]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  const submitText = async () => {
    if (!textTitle.trim() || !textContent.trim()) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("title", textTitle);
    fd.append("content", textContent);
    try {
      await axios.post(`${API}/projects/${project.id}/ingest_text`, fd);
      setTextTitle(""); setTextContent("");
      await reload();
      toast.show("Text ingested");
    } catch (e) { toast.show("Failed", "err"); }
    setBusy(false);
  };

  const del = async (id) => {
    await axios.delete(`${API}/raw_files/${id}`);
    await reload();
  };

  return (
    <div className="p-8 max-w-[1400px]">
      <Header title="Raw Data" subtitle="Drop text, CSV, JSON, audio (.mp3/.wav) or video (.mp4). Audio/video is transcribed with Whisper. Everything is chunked and stored in DeepLake." />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
        <div {...getRootProps()} className={`dropzone ${isDragActive ? "active" : ""}`} data-testid="dropzone">
          <input {...getInputProps()} data-testid="dropzone-input" />
          <Upload size={28} className="text-zinc-400 mb-3" />
          <div className="font-display font-semibold text-zinc-900 mb-1">Drop files or click to browse</div>
          <div className="text-xs text-zinc-500 font-mono">txt · md · csv · json · jsonl · mp3 · wav · mp4</div>
          {uploading && <div className="mt-3 text-xs text-zinc-600 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Uploading…</div>}
        </div>
        <div className="card p-5">
          <div className="overline mb-3">Paste raw text</div>
          <label className="label">Title</label>
          <input data-testid="paste-title" className="input mb-3" placeholder="my_notes.txt" value={textTitle} onChange={e => setTextTitle(e.target.value)} />
          <label className="label">Content</label>
          <textarea data-testid="paste-content" className="input input-mono min-h-[140px]" placeholder="Paste any raw text here…" value={textContent} onChange={e => setTextContent(e.target.value)} />
          <button data-testid="paste-submit" className="btn-primary mt-3" onClick={submitText} disabled={busy}>
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Ingest text
          </button>
        </div>
      </div>

      <div className="flex items-center justify-between mb-3">
        <div className="overline">Ingested files ({files.length})</div>
        <button className="btn-ghost" onClick={reload} data-testid="refresh-raw"><RefreshCw size={14} /> Refresh</button>
      </div>
      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-zinc-50 border-b border-zinc-200 text-left">
              <th className="p-2.5 font-medium text-zinc-500 text-xs uppercase tracking-wide">File</th>
              <th className="p-2.5 font-medium text-zinc-500 text-xs uppercase tracking-wide">Type</th>
              <th className="p-2.5 font-medium text-zinc-500 text-xs uppercase tracking-wide">Status</th>
              <th className="p-2.5 font-medium text-zinc-500 text-xs uppercase tracking-wide">Tokens</th>
              <th className="p-2.5 font-medium text-zinc-500 text-xs uppercase tracking-wide">Snippets</th>
              <th className="p-2.5 w-10"></th>
            </tr>
          </thead>
          <tbody>
            {files.map(f => (
              <tr key={f.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`raw-row-${f.id}`}>
                <td className="p-2.5 font-mono text-[13px] text-zinc-900 flex items-center gap-2">
                  {fileIcon(f.file_type)} {f.filename}
                </td>
                <td className="p-2.5"><span className="badge badge-zinc">{f.file_type}</span></td>
                <td className="p-2.5"><StatusBadge s={f.status} /></td>
                <td className="p-2.5 font-mono text-xs">{f.token_count.toLocaleString()}</td>
                <td className="p-2.5 font-mono text-xs">{f.snippet_count}</td>
                <td className="p-2.5 text-right">
                  <button className="btn-ghost !p-1.5" onClick={() => del(f.id)} data-testid={`del-raw-${f.id}`}><Trash2 size={14} className="text-zinc-400 hover:text-red-600" /></button>
                </td>
              </tr>
            ))}
            {files.length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center text-zinc-400 text-sm">No files yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------- Dataset Architect ----------
function ArchitectView({ project, toast, goTo }) {
  const [instruction, setInstruction] = useState("Generate diverse Q&A pairs that teach an assistant the key facts from the raw context.");
  const [numRows, setNumRows] = useState(8);
  const [useContext, setUseContext] = useState(true);
  const [providerList, setProviderList] = useState([]);
  const [providerId, setProviderId] = useState("");
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  useEffect(() => {
    axios.get(`${API}/providers`).then(r => {
      setProviderList(r.data.providers || []);
      setProviderId(prev => prev || r.data.default_id || "");
    }).catch(() => {});
  }, []);

  const run = async () => {
    setBusy(true); setLastResult(null);
    try {
      const payload = {
        project_id: project.id,
        instruction,
        num_rows: Number(numRows),
        use_raw_context: useContext,
        model_provider: "lmstudio", // legacy compat field — always accepted
      };
      if (providerId) payload.provider_id = providerId;
      const r = await axios.post(`${API}/projects/${project.id}/architect`, payload);
      setLastResult(r.data);
      toast.show(`Generated ${r.data.generated} rows`);
    } catch (e) {
      toast.show(e.response?.data?.error || e.response?.data?.detail?.slice(0, 120) || "Architect failed", "err");
    }
    setBusy(false);
  };

  return (
    <div className="p-8 max-w-[1400px]">
      <Header title="Dataset Architect" subtitle="Describe the dataset you want. The Architect will use your selected AI provider to ground on your DeepLake raw context and produce JSON-structured fine-tuning rows." />
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-6">
        <div className="card p-5">
          <label className="label">Intent / Instruction</label>
          <textarea data-testid="architect-instruction" className="input min-h-[140px]" value={instruction} onChange={e => setInstruction(e.target.value)} />
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div>
              <label className="label">How many rows</label>
              <input data-testid="architect-num" type="number" min="1" max="50" className="input input-mono" value={numRows} onChange={e => setNumRows(e.target.value)} />
            </div>
            <div>
              <label className="label">Model provider</label>
              <select data-testid="architect-provider" className="input" value={providerId} onChange={e => setProviderId(e.target.value)}>
                {providerList.length === 0 && <option value="">Loading providers…</option>}
                {providerList.map(p => (
                  <option key={p.id} value={p.id}>{p.name} ({p.type}{p.model ? ` · ${p.model}` : ""}){p.is_default === 1 ? " ★" : ""}</option>
                ))}
              </select>
            </div>
          </div>
          <label className="flex items-center gap-2 mt-4 text-sm cursor-pointer" data-testid="architect-ctx-label">
            <input data-testid="architect-use-ctx" type="checkbox" checked={useContext} onChange={e => setUseContext(e.target.checked)} className="w-4 h-4 accent-black" />
            <span>Ground on raw context (DeepLake top-8)</span>
          </label>
          <div className="mt-5 flex gap-2">
            <button data-testid="architect-run" className="btn-primary" onClick={run} disabled={busy || !instruction.trim()}>
              {busy ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />}
              Generate rows
            </button>
            <button className="btn-outline" onClick={() => goTo("editor")} data-testid="architect-open-editor">
              Open editor <ChevronRight size={14} />
            </button>
          </div>
        </div>
        <div className="card p-5 min-h-[280px]">
          <div className="overline mb-3">Last run</div>
          {!lastResult && !busy && <div className="text-sm text-zinc-400">No output yet. Hit “Generate rows”.</div>}
          {busy && <div className="text-sm text-zinc-500 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Architect is thinking…</div>}
          {lastResult && (
            <>
              <div className="text-sm mb-3">
                <span className="badge badge-green"><Check size={11} /> {lastResult.generated} rows saved</span>
              </div>
              <div className="space-y-3 max-h-[500px] overflow-y-auto scrollbar-thin">
                {lastResult.rows.map((r, i) => (
                  <div key={r.id} className="border border-zinc-200 rounded p-3 text-[13px]" data-testid={`gen-row-${i}`}>
                    <div className="font-mono text-zinc-500 text-[11px] mb-1">#{i + 1} · {r.token_count} tok</div>
                    <div className="font-medium text-zinc-900 mb-1.5">Q: {r.instruction}</div>
                    <div className="text-zinc-700 whitespace-pre-wrap">A: {r.output}</div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------- Dataset Editor ----------
function EditorView({ project, toast }) {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const empty = { instruction: "", input: "", output: "", system: "", tags: [], source: "manual" };

  const reload = useCallback(async () => {
    const r = await axios.get(`${API}/projects/${project.id}/rows`);
    setRows(r.data);
  }, [project.id]);
  useEffect(() => { reload(); }, [reload]);

  const save = async (row) => {
    await axios.post(`${API}/projects/${project.id}/rows`, row);
    setEditing(null); setCreating(false);
    await reload();
    toast.show("Saved");
  };
  const del = async (id) => {
    await axios.delete(`${API}/rows/${id}`);
    await reload();
  };
  const clearAll = async () => {
    if (!window.confirm("Delete ALL dataset rows?")) return;
    await axios.post(`${API}/projects/${project.id}/rows/clear`);
    await reload();
  };

  return (
    <div className="p-8 max-w-[1600px]">
      <Header
        title="Dataset Editor"
        subtitle={`${rows.length} rows · ${rows.reduce((s, r) => s + r.token_count, 0).toLocaleString()} tokens total`}
        right={
          <div className="flex gap-2">
            <button className="btn-outline" onClick={reload} data-testid="editor-refresh"><RefreshCw size={14} /> Refresh</button>
            <button className="btn-danger" onClick={clearAll} disabled={!rows.length} data-testid="editor-clear"><Trash2 size={14} /> Clear</button>
            <button className="btn-primary" onClick={() => setCreating(true)} data-testid="editor-add"><Plus size={14} /> Add row</button>
          </div>
        }
      />
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-zinc-50 border-b border-zinc-200 text-left">
              <th className="p-2.5 overline w-12">#</th>
              <th className="p-2.5 overline">Instruction</th>
              <th className="p-2.5 overline">Output</th>
              <th className="p-2.5 overline w-24">Source</th>
              <th className="p-2.5 overline w-20">Tokens</th>
              <th className="p-2.5 w-24"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.id} className="border-b border-zinc-100 hover:bg-zinc-50 align-top" data-testid={`ds-row-${i}`}>
                <td className="p-2.5 font-mono text-zinc-400 text-xs">{i + 1}</td>
                <td className="p-2.5 font-mono text-[12.5px] max-w-[360px] truncate" title={r.instruction}>{r.instruction}</td>
                <td className="p-2.5 font-mono text-[12.5px] max-w-[500px] truncate text-zinc-700" title={r.output}>{r.output}</td>
                <td className="p-2.5"><span className={`badge ${r.source === "synthetic" ? "badge-blue" : r.source === "imported" ? "badge-amber" : "badge-zinc"}`}>{r.source}</span></td>
                <td className="p-2.5 font-mono text-xs">{r.token_count}</td>
                <td className="p-2.5 text-right whitespace-nowrap">
                  <button className="btn-ghost !p-1.5" onClick={() => setEditing(r)} data-testid={`edit-row-${i}`}><Settings2 size={14} /></button>
                  <button className="btn-ghost !p-1.5" onClick={() => del(r.id)} data-testid={`del-row-${i}`}><Trash2 size={14} className="text-zinc-400 hover:text-red-600" /></button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="p-10 text-center text-zinc-400">No rows. Use the Architect or add manually.</td></tr>}
          </tbody>
        </table>
        </div>
      </div>
      {(editing || creating) && (
        <RowEditor
          initial={editing || empty}
          onCancel={() => { setEditing(null); setCreating(false); }}
          onSave={save}
        />
      )}
    </div>
  );
}

function RowEditor({ initial, onCancel, onSave }) {
  const [v, setV] = useState(initial);
  return (
    <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4" data-testid="row-editor-modal">
      <div className="bg-white rounded-lg w-full max-w-2xl border border-zinc-200 max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between">
          <h3 className="font-display font-semibold text-lg">{initial.id ? "Edit row" : "New row"}</h3>
          <button className="btn-ghost !p-1.5" onClick={onCancel} data-testid="row-editor-close"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div><label className="label">System (optional)</label><input className="input" value={v.system} onChange={e => setV({ ...v, system: e.target.value })} data-testid="re-system" /></div>
          <div><label className="label">Instruction *</label><textarea className="input min-h-[80px]" value={v.instruction} onChange={e => setV({ ...v, instruction: e.target.value })} data-testid="re-instruction" /></div>
          <div><label className="label">Input (optional)</label><textarea className="input min-h-[60px]" value={v.input} onChange={e => setV({ ...v, input: e.target.value })} data-testid="re-input" /></div>
          <div><label className="label">Output *</label><textarea className="input min-h-[120px]" value={v.output} onChange={e => setV({ ...v, output: e.target.value })} data-testid="re-output" /></div>
          <div><label className="label">Tags (comma-separated)</label><input className="input input-mono" value={v.tags.join(",")} onChange={e => setV({ ...v, tags: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })} data-testid="re-tags" /></div>
        </div>
        <div className="px-5 py-4 border-t border-zinc-200 flex justify-end gap-2">
          <button className="btn-outline" onClick={onCancel}>Cancel</button>
          <button className="btn-primary" onClick={() => onSave(v)} disabled={!v.instruction || !v.output} data-testid="re-save"><Check size={14} /> Save row</button>
        </div>
      </div>
    </div>
  );
}

// ---------- Search ----------
function SearchView({ project }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snapshot, setSnapshot] = useState([]);

  useEffect(() => {
    axios.get(`${API}/projects/${project.id}/snippets?limit=30`).then(r => setSnapshot(r.data));
  }, [project.id]);

  const run = async () => {
    if (!q.trim()) return;
    setLoading(true);
    const r = await axios.get(`${API}/projects/${project.id}/search`, { params: { q, k: 10 } });
    setResults(r.data.results);
    setLoading(false);
  };

  const list = q && results.length ? results : snapshot;

  return (
    <div className="p-8 max-w-[1200px]">
      <Header title="DeepLake Search" subtitle={`Semantic-ish search over ${snapshot.length}+ raw snippets stored as embeddings in DeepLake.`} />
      <div className="card p-4 mb-5 flex gap-2">
        <input data-testid="search-input" className="input input-mono" placeholder="Search your raw data…" value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === "Enter" && run()} />
        <button className="btn-primary" onClick={run} disabled={loading} data-testid="search-btn">
          {loading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />} Search
        </button>
      </div>
      <div className="space-y-2.5">
        {list.map((r, i) => (
          <div key={r.snippet_id} className="card p-4" data-testid={`snippet-${i}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="font-mono text-[11px] text-zinc-500">{r.source}</span>
              {r.score !== undefined && <span className="badge badge-blue">score {r.score}</span>}
            </div>
            <div className="font-mono text-[13px] text-zinc-800 whitespace-pre-wrap break-words">{r.text}</div>
          </div>
        ))}
        {!list.length && <div className="text-center text-zinc-400 py-12 text-sm">Nothing in DeepLake yet. Ingest some raw data first.</div>}
      </div>
    </div>
  );
}

// ---------- AI Providers ----------
const PROVIDER_TYPES = [
  { value: "openai_compatible", label: "OpenAI-compatible (LM Studio, Ollama, vLLM, OpenRouter…)" },
  { value: "anthropic", label: "Anthropic Claude" },
  { value: "openai", label: "OpenAI" },
  { value: "gemini", label: "Google Gemini" },
];

const DEFAULT_BASE_URLS = {
  anthropic: "https://api.anthropic.com",
  openai: "https://api.openai.com/v1",
  gemini: "https://generativelanguage.googleapis.com",
  openai_compatible: "http://localhost:1234/v1",
};

const EMPTY_FORM = { name: "", type: "openai_compatible", base_url: "", model: "", api_key: "", is_default: false };

function ProvidersView({ toast }) {
  const [list, setList] = useState([]);
  const [defaultId, setDefaultId] = useState(null);
  const [editing, setEditing] = useState(null); // null | {id?} form state
  const [busy, setBusy] = useState(false);
  const [test, setTest] = useState(null);
  const [availableModels, setAvailableModels] = useState([]);
  const [msg, setMsg] = useState("");
  const [chat, setChat] = useState([]);
  const [chatProvider, setChatProvider] = useState("");
  const [sending, setSending] = useState(false);

  const reload = useCallback(async () => {
    const r = await axios.get(`${API}/providers`);
    setList(r.data.providers || []);
    setDefaultId(r.data.default_id);
    setChatProvider(prev => prev || r.data.default_id || "");
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const startAdd = () => { setEditing({ ...EMPTY_FORM }); setTest(null); setAvailableModels([]); };
  const startEdit = (p) => {
    setEditing({ id: p.id, name: p.name, type: p.type, base_url: p.base_url, model: p.model, api_key: "", is_default: p.is_default === 1 });
    setTest(null); setAvailableModels([]);
  };

  const onTypeChange = (type) => {
    // Prefill the per-type default base URL unless the user already typed a custom one
    const known = Object.values(DEFAULT_BASE_URLS);
    const base_url = !editing.base_url || known.includes(editing.base_url) ? DEFAULT_BASE_URLS[type] : editing.base_url;
    setEditing({ ...editing, type, base_url });
  };

  const save = async () => {
    if (!editing.name.trim()) { toast.show("Provider needs a name", "err"); return; }
    setBusy(true);
    try {
      const payload = { ...editing };
      if (editing.id && !payload.api_key) delete payload.api_key; // empty = keep stored key
      if (editing.id) await axios.put(`${API}/providers/${editing.id}`, payload);
      else await axios.post(`${API}/providers`, payload);
      toast.show("Saved");
      setEditing(null);
      await reload();
    } catch (e) {
      toast.show(e.response?.data?.error || "Save failed", "err");
    }
    setBusy(false);
  };

  const runTest = async (p) => {
    setBusy(true); setTest(null);
    try {
      // In-form test: save first so unsaved values are what gets tested.
      let target = p;
      if (editing && !editing.id) { await save(); return; }
      if (editing?.id) target = { ...p, id: editing.id };
      const r = await axios.post(`${API}/providers/${target.id}/test`);
      setTest(r.data);
      if (r.data.ok) setAvailableModels(r.data.models || []);
    } catch (e) {
      setTest({ ok: false, error: e.response?.data?.error || String(e) });
    }
    setBusy(false);
  };

  const setDefault = async (id) => {
    await axios.post(`${API}/providers/${id}/set_default`);
    await reload();
    toast.show("Default provider updated");
  };

  const del = async (p) => {
    if (!window.confirm(`Delete provider "${p.name}"?`)) return;
    try {
      await axios.delete(`${API}/providers/${p.id}`);
      await reload();
      toast.show("Deleted");
    } catch (e) {
      toast.show(e.response?.data?.error || "Delete failed", "err");
    }
  };

  const send = async () => {
    if (!msg.trim()) return;
    setSending(true);
    const history = chat.map(c => ({ role: c.role, content: c.content }));
    const newChat = [...chat, { role: "user", content: msg }];
    setChat(newChat); setMsg("");
    try {
      const r = await axios.post(`${API}/chat`, {
        provider_id: chatProvider || undefined,
        message: msg, system: "You are a helpful assistant.", history,
      });
      if (r.data.ok) setChat([...newChat, { role: "assistant", content: r.data.reply }]);
      else setChat([...newChat, { role: "assistant", content: `[error] ${r.data.error}` }]);
    } catch (e) {
      setChat([...newChat, { role: "assistant", content: `[error] ${e.response?.data?.error || e}` }]);
    }
    setSending(false);
  };

  return (
    <div className="p-8 max-w-[1400px]">
      <Header title="AI Providers" subtitle="Connect any model backend — Anthropic Claude, OpenAI, Google Gemini, or any OpenAI-compatible endpoint (LM Studio, Ollama, vLLM, OpenRouter). Expose localhost with ngrok and paste the tunnel URL as the base URL." />
      <div className="grid grid-cols-1 lg:grid-cols-[520px_1fr] gap-6">
        <div className="space-y-4">
          <div className="card p-5" data-testid="prov-list">
            <div className="overline mb-3 flex items-center justify-between">
              Providers
              <button className="btn-outline !py-1 !px-2 text-xs" onClick={startAdd} data-testid="prov-add"><Plus size={13} /> Add</button>
            </div>
            {list.length === 0 && <div className="text-sm text-zinc-400">No providers yet.</div>}
            <div className="space-y-2">
              {list.map(p => (
                <div key={p.id} className="border border-zinc-200 rounded-md p-3 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-zinc-900">{p.name}</span>
                    <span className="badge">{p.type}</span>
                    {p.is_default === 1 && <span className="badge badge-green">Default</span>}
                  </div>
                  <div className="text-xs text-zinc-500 font-mono mt-1 truncate">
                    {p.base_url || DEFAULT_BASE_URLS[p.type]} · {p.model || "no model set"}{p.has_key ? ` · key ${p.api_key_masked}` : ""}
                  </div>
                  <div className="flex gap-1.5 mt-2">
                    <button className="btn-ghost !py-1 !px-2 text-xs" onClick={() => startEdit(p)} data-testid={`prov-edit-${p.id}`}><Settings2 size={12} /> Edit</button>
                    <button className="btn-ghost !py-1 !px-2 text-xs" onClick={() => runTest(p)} data-testid={`prov-test-${p.id}`}>Test</button>
                    {p.is_default !== 1 && <button className="btn-ghost !py-1 !px-2 text-xs" onClick={() => setDefault(p.id)} data-testid={`prov-default-${p.id}`}>Set default</button>}
                    <button className="btn-ghost !py-1 !px-2 text-xs text-red-600" onClick={() => del(p)} data-testid={`prov-delete-${p.id}`}><Trash2 size={12} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {editing && (
            <div className="card p-5">
              <div className="overline mb-3">{editing.id ? "Edit provider" : "Add provider"}</div>
              <label className="label">Name</label>
              <input data-testid="prov-name" className="input" value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} placeholder="e.g. Claude Sonnet, Local LM Studio" />
              <label className="label mt-3">Type</label>
              <select data-testid="prov-type" className="input" value={editing.type} onChange={e => onTypeChange(e.target.value)}>
                {PROVIDER_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
              <label className="label mt-3">Base URL</label>
              <input data-testid="prov-base-url" className="input input-mono" value={editing.base_url} onChange={e => setEditing({ ...editing, base_url: e.target.value })} placeholder={DEFAULT_BASE_URLS[editing.type]} />
              <div className="text-[11px] text-zinc-500 font-mono mt-1">
                {editing.type === "openai_compatible"
                  ? "e.g. http://localhost:1234/v1 or https://xxx.ngrok.io/v1"
                  : `Leave empty to use ${DEFAULT_BASE_URLS[editing.type]}`}
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div>
                  <label className="label">Model</label>
                  {availableModels.length > 0 ? (
                    <select data-testid="prov-model" className="input" value={editing.model} onChange={e => setEditing({ ...editing, model: e.target.value })}>
                      <option value="">— select —</option>
                      {availableModels.map(m => <option key={m.id} value={m.id}>{m.id}</option>)}
                    </select>
                  ) : (
                    <input data-testid="prov-model" className="input input-mono" value={editing.model} onChange={e => setEditing({ ...editing, model: e.target.value })} placeholder={editing.type === "anthropic" ? "claude-sonnet-4-5" : editing.type === "gemini" ? "gemini-2.0-flash" : editing.type === "openai" ? "gpt-4o-mini" : "Test connection to load models"} />
                  )}
                </div>
                <div>
                  <label className="label">API Key</label>
                  <input data-testid="prov-key" type="password" className="input input-mono" value={editing.api_key} onChange={e => setEditing({ ...editing, api_key: e.target.value })} placeholder={editing.id ? "unchanged unless typed" : "required for cloud providers"} />
                </div>
              </div>
              <label className="flex items-center gap-2 mt-3 text-sm cursor-pointer">
                <input type="checkbox" className="w-4 h-4 accent-black" checked={editing.is_default} onChange={e => setEditing({ ...editing, is_default: e.target.checked })} />
                <span>Make default provider</span>
              </label>
              <div className="flex gap-2 mt-4">
                <button className="btn-primary" onClick={save} disabled={busy} data-testid="prov-save">{busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Save</button>
                {editing.id && <button className="btn-outline" onClick={() => runTest({ id: editing.id })} disabled={busy} data-testid="prov-test">Test connection</button>}
                <button className="btn-ghost" onClick={() => { setEditing(null); setTest(null); }} disabled={busy}><X size={14} /> Cancel</button>
              </div>
              {!editing.id && <div className="text-[11px] text-zinc-500 mt-2">Save first, then Test — testing uses the stored connection values.</div>}
              {test && (
                <div className={`mt-3 p-3 rounded-md border text-xs font-mono ${test.ok ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-red-50 border-red-200 text-red-700"}`} data-testid="prov-test-result">
                  {test.ok ? `✓ Connected. Models: ${JSON.stringify((test.models || []).map(m => m.id)).slice(0, 200)}` : `✗ ${test.error}`}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="card flex flex-col h-[600px]">
          <div className="px-5 py-3 border-b border-zinc-200 overline flex items-center justify-between gap-2">
            Sanity-check chat
            <div className="flex items-center gap-2">
              <select data-testid="prov-chat-provider" className="input !py-1 !text-xs max-w-[180px]" value={chatProvider} onChange={e => setChatProvider(e.target.value)}>
                {list.map(p => <option key={p.id} value={p.id}>{p.name}{p.is_default === 1 ? " ★" : ""}</option>)}
              </select>
              <button className="btn-ghost !p-1.5" onClick={() => setChat([])}><Trash2 size={13} /></button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto scrollbar-thin p-5 space-y-3" data-testid="lm-chat">
            {chat.length === 0 && <div className="text-sm text-zinc-400">Pick a provider and talk to its model — verify answers before training.</div>}
            {chat.map((c, i) => (
              <div key={i} className={`rounded-md p-3 text-[13.5px] whitespace-pre-wrap ${c.role === "user" ? "bg-white border border-zinc-200 ml-10" : "bg-zinc-100 mr-10"}`} data-testid={`chat-msg-${i}`}>
                <div className="overline mb-1.5 text-[10px]">{c.role}</div>
                {c.content}
              </div>
            ))}
            {sending && <div className="text-sm text-zinc-500 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> thinking…</div>}
          </div>
          <div className="border-t border-zinc-200 p-3 flex gap-2">
            <input data-testid="lm-chat-input" className="input" placeholder="Message…" value={msg} onChange={e => setMsg(e.target.value)} onKeyDown={e => e.key === "Enter" && !sending && send()} />
            <button className="btn-primary" onClick={send} disabled={sending || !msg.trim()} data-testid="lm-chat-send"><Send size={14} /></button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- Export ----------
const EXPORT_PROFILES = [
  { key: "alpaca", name: "Alpaca (JSON)", desc: "Unsloth / LoRA fine-tuning — {instruction, input, output}", ext: "json" },
  { key: "sharegpt", name: "ShareGPT (JSON)", desc: "Unsloth ShareGPT format with conversations[from,value]", ext: "json" },
  { key: "jsonl_chatml", name: "ChatML JSONL", desc: "AnythingLLM / Open WebUI — {messages:[{role,content}]}", ext: "jsonl" },
  { key: "openai_finetune", name: "OpenAI Fine-tune JSONL", desc: "Ready for OpenAI fine-tuning API", ext: "jsonl" },
  { key: "csv", name: "CSV", desc: "Flat spreadsheet view for review", ext: "csv" },
];

function ExportView({ project, toast }) {
  const [profile, setProfile] = useState("alpaca");
  const [preview, setPreview] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    axios.get(`${API}/projects/${project.id}`).then(r => setStats(r.data));
    axios.get(`${API}/projects/${project.id}/export_preview`, { params: { profile, limit: 2 } }).then(r => setPreview(r.data));
  }, [project.id, profile]);

  const download = async () => {
    try {
      const r = await axios.post(`${API}/projects/${project.id}/export`, { project_id: project.id, profile }, { responseType: "blob" });
      const cd = r.headers["content-disposition"] || "";
      const m = /filename="([^"]+)"/.exec(cd);
      const fname = m ? m[1] : `dataset.${EXPORT_PROFILES.find(p => p.key === profile).ext}`;
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = fname; a.click();
      URL.revokeObjectURL(url);
      toast.show("Downloaded");
    } catch (e) {
      toast.show(e.response?.status === 400 ? "No dataset rows yet" : "Export failed", "err");
    }
  };

  return (
    <div className="p-8 max-w-[1400px]">
      <Header title="Export" subtitle="Download your dataset in the format required by your training target." />
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Stat label="Dataset rows" value={stats.dataset_rows} />
          <Stat label="Total tokens" value={(stats.total_tokens || 0).toLocaleString()} />
          <Stat label="Raw files" value={stats.raw_files} />
          <Stat label="DeepLake snippets" value={stats.snippets_in_deeplake} />
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-6">
        <div className="space-y-2">
          {EXPORT_PROFILES.map(p => (
            <button
              key={p.key}
              onClick={() => setProfile(p.key)}
              data-testid={`export-profile-${p.key}`}
              className={`w-full text-left card p-4 transition-colors ${profile === p.key ? "border-black ring-2 ring-black/10" : "hover:bg-zinc-50"}`}
            >
              <div className="flex items-center justify-between">
                <div className="font-display font-semibold text-zinc-950">{p.name}</div>
                <span className="kbd">.{p.ext}</span>
              </div>
              <div className="text-xs text-zinc-500 mt-1">{p.desc}</div>
            </button>
          ))}
          <button className="btn-primary w-full justify-center mt-2" onClick={download} data-testid="export-download"><Download size={15} /> Download dataset</button>
        </div>
        <div className="card p-5">
          <div className="overline mb-3">Preview (first 2 rows, {profile})</div>
          <pre className="font-mono text-[12px] text-zinc-800 whitespace-pre-wrap bg-zinc-50 p-3 rounded border border-zinc-200 overflow-x-auto max-h-[500px]" data-testid="export-preview">
            {preview ? JSON.stringify(preview, null, 2) : "Loading…"}
          </pre>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="card p-4">
      <div className="overline mb-1">{label}</div>
      <div className="font-display font-bold text-2xl text-zinc-950">{value}</div>
    </div>
  );
}

// ---------- App ----------
export default function App() {
  const [projects, setProjects] = useState([]);
  const [project, setProject] = useState(null);
  const [view, setView] = useState("projects");
  const toast = useToast();

  const reload = useCallback(async () => {
    const r = await axios.get(`${API}/projects`);
    setProjects(r.data);
    // refresh selected
    if (project) {
      const found = r.data.find(p => p.id === project.id);
      setProject(found || null);
    }
  }, [project]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, []);

  const onOpen = (p) => { setProject(p); setView("raw"); };

  return (
    <div className="App min-h-screen flex bg-zinc-50" data-testid="app-root">
      <Sidebar view={view} setView={setView} project={project} />
      <main className="flex-1 min-w-0">
        {view === "projects" && <ProjectsView projects={projects} reload={reload} onOpen={onOpen} toast={toast} />}
        {view === "raw" && project && <RawDataView project={project} toast={toast} />}
        {view === "architect" && project && <ArchitectView project={project} toast={toast} goTo={setView} />}
        {view === "editor" && project && <EditorView project={project} toast={toast} />}
        {view === "search" && project && <SearchView project={project} />}
        {view === "providers" && <ProvidersView toast={toast} />}
        {view === "export" && project && <ExportView project={project} toast={toast} />}
        {view !== "projects" && view !== "providers" && !project && (
          <div className="p-12 text-center text-zinc-400">Select a project first.</div>
        )}
      </main>
      {toast.node}
    </div>
  );
}
